// §4.5's explanation drawer, the module's signature surface (spec 03 §4.5,
// design-system §3.15). It explains one planned order in five stacked
// sections: Demand (what drives it, with the walk up the chain), Quantity math
// (the waterfall), Date math (the timeline), Drives (the orders this one
// drives) and the action-message box. The same payload feeds the agent's
// `planning.explain_planned_order`. Opened from `?order=<id>` on the list; on
// a phone it is a full-screen sheet.

import { useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { StatusChip } from "../../../../components/status-chip";
import { ErrorState, Skeleton } from "../../../../components/states";
import { Banner } from "../../../../components/toast";
import { formatTimestamp } from "../../../../lib/datetime";
import { facilityTodayIso } from "../../../../lib/business-dates";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { useCalendarExceptions, useCalendarWorkCenters } from "../hooks/calendar-queries";
import {
  PLANNED_ORDER_REASON_MAX,
  acceptActionMessage,
  cancelPlannedOrder,
  convertPlannedOrders,
  dismissActionMessage,
  firmPlannedOrders,
  useMrpRefresh,
  usePlannedOrder,
  type ExpectedActionMessage,
  type ExplanationDemandItem,
  type ExplanationLabel,
  type PegEdge,
  type PlannedOrder,
  type PlannedOrderDetail,
} from "../hooks/mrp-queries";
import { useMemberNames } from "../hooks/mps-queries";
import { usePlanningPermissions } from "../hooks/permissions";
import { useProductIndex, useUoms, type Product } from "../hooks/planning-queries";
import {
  dateTimeline,
  isZeroQty,
  quantityWaterfall,
  shortId,
  type TimelineRow,
  type WaterfallRow,
} from "../lib/mrp-lib";
import { workCenterLabel } from "../lib/calendar-lib";
import { formatDate, useOffline } from "../lib/planning-lib";
import { usePlanningFacility } from "../planning-layout";
import { OrderLabel } from "./demand-bits";
import {
  ActionMessageChip,
  ConvertedDocument,
  ExactQty,
  PlannedOrderKindChip,
  PlannedOrderStatusChip,
} from "./mrp-bits";

/** The note keys the kernel writes (spec 03 §2.10); any other key reads as itself. */
const QTY_NOTES = new Set([
  "exceeds_approved_mps",
  "scrap_applied_on_children",
  "position_below_zero",
  "firmed_quantity_kept",
  "commitment_output_in_on_hand",
]);
const DATE_NOTES = new Set([
  "clamped_to_today",
  "firmed_dates_kept",
  "outsourced_step_not_timed",
  "source_uncalendared",
  "work_center_closed_in_window",
  "work_center_uncalendared",
]);
const REASON_KEYS = new Set([
  "no_need_in_horizon",
  "release_in_past",
  "need_earlier",
  "need_later",
  "share_below_qty",
  "need_uncovered_inside_fence",
]);

export interface PlannedOrderDrawerProps {
  readonly id: string;
  readonly onClose: () => void;
  /** Switches the drawer to another planned order (a parent or a driven order). */
  readonly onOpen: (id: string) => void;
}

export function PlannedOrderDrawer({ id, onClose, onOpen }: PlannedOrderDrawerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const detail = usePlannedOrder(id);
  const [pending, setPending] = useState(false);

  if (detail.error instanceof UnauthorizedError) {
    void navigate({ to: "/login" });
    return null;
  }

  const order = detail.data?.order;
  const heading =
    order === undefined ? (
      t("planning.orders.drawer.loadingTitle")
    ) : (
      <span className="planning-drawer-title">
        <span data-testid="planning-drawer-po-no">{order.po_no}</span>
        <PlannedOrderKindChip kind={order.kind} />
        <PlannedOrderStatusChip status={order.status} />
      </span>
    );

  let body: ReactNode;
  if (detail.error !== null) {
    const gone =
      detail.error instanceof RequestError &&
      (detail.error.code === "PLANNED_ORDER_NOT_FOUND" || detail.error.httpStatus === 404);
    body = gone ? (
      <p className="planning-note" data-testid="planning-drawer-gone">
        {t("planning.orders.drawer.gone")}
      </p>
    ) : (
      <ErrorState
        title={t("planning.orders.drawer.loadError")}
        retryLabel={t("planning.common.retry")}
        onRetry={() => void detail.refetch()}
        requestId={requestIdOf(detail.error)}
        requestIdLabel={t("planning.common.requestId")}
      />
    );
  } else if (detail.data === undefined) {
    body = <DrawerSkeleton />;
  } else {
    body = <DrawerBody detail={detail.data} onOpen={onOpen} onPending={setPending} />;
  }

  return (
    <Modal
      label={order === undefined ? t("planning.orders.drawer.label") : order.po_no}
      heading={heading}
      closeLabel={t("planning.common.close")}
      onClose={onClose}
      pending={pending}
      className="planning-drawer planning-order-sheet"
      testId="planning-order-drawer"
    >
      {body}
    </Modal>
  );
}

/** Each section reserves its place while the order loads (design-system §3.15). */
function DrawerSkeleton() {
  const { t } = useTranslation();
  return (
    <div role="status" aria-label={t("planning.orders.drawer.loading")}>
      {(["demand", "qty", "date", "drives"] as const).map((s) => (
        <section
          key={s}
          className="planning-drawer-section"
          data-testid={`planning-drawer-skeleton-${s}`}
        >
          <Skeleton width="30%" height="1.1rem" />
          <Skeleton height="0.9rem" />
          <Skeleton height="0.9rem" />
          <Skeleton width="70%" height="0.9rem" />
        </section>
      ))}
    </div>
  );
}

function DrawerBody({
  detail,
  onOpen,
  onPending,
}: {
  detail: PlannedOrderDetail;
  onOpen: (id: string) => void;
  onPending: (busy: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const { facilities } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const order = detail.order;
  const facility = facilities.find((f) => f.id === order.facility_id);
  const zone = facility?.timezone;
  const productIds = useMemo(() => {
    const ids = [order.product_id];
    for (const l of detail.labels) if (l.product_id !== null) ids.push(l.product_id);
    for (const e of [...detail.pegs_up, ...detail.pegs_down]) {
      if (e.order !== null) ids.push(e.order.product_id);
    }
    return ids;
  }, [order.product_id, detail.labels, detail.pegs_up, detail.pegs_down]);
  const products = useProductIndex(productIds);
  const uoms = useUoms();
  const unit = uoms.data?.items.find((u) => u.id === order.uom_id)?.code;
  const members = useMemberNames(perms.canReadMembers);
  const who = (userId: string | null) =>
    userId === null ? "" : (members.byId.get(userId) ?? shortId(userId));
  const facilityName = (fid: string | null) =>
    fid === null ? "" : (facilities.find((f) => f.id === fid)?.name ?? shortId(fid));
  const product = products.byId.get(order.product_id);

  return (
    <div className="planning-order-body" data-order-id={order.id} data-status={order.status}>
      <section className="planning-drawer-section" data-testid="planning-drawer-facts">
        <dl className="planning-facts">
          <Fact label={t("planning.orders.columns.product")}>
            <ProductName product={product} id={order.product_id} />
          </Fact>
          <Fact label={t("planning.orders.columns.qty")}>
            <ExactQty qty={order.qty} unit={unit} testId="planning-drawer-order-qty" />
          </Fact>
          <Fact label={t("planning.orders.columns.due")}>
            <span data-testid="planning-drawer-due" data-date={order.due_date}>
              {formatDate(order.due_date, i18n.language)}
            </span>
            {order.late ? <LateChip /> : null}
          </Fact>
          <Fact label={t("planning.orders.columns.start")}>
            {formatDate(order.start_date, i18n.language)}
          </Fact>
          <Fact label={t("planning.orders.drawer.release")}>
            {formatDate(order.release_date, i18n.language)}
          </Fact>
          {order.source_facility_id !== null ? (
            <Fact label={t("planning.orders.drawer.source")}>
              {facilityName(order.source_facility_id)}
            </Fact>
          ) : null}
          {order.firmed_at !== null ? (
            <Fact label={t("planning.orders.drawer.firmed")}>
              {t("planning.orders.drawer.byAt", {
                who: who(order.firmed_by),
                time: formatTimestamp(order.firmed_at, i18n.language, zone),
              })}
            </Fact>
          ) : null}
          {order.converted_kind !== null && order.converted_ref_id !== null ? (
            <Fact label={t("planning.orders.drawer.converted")}>
              <span className="planning-chips" data-testid="planning-drawer-converted">
                <ConvertedDocument
                  kind={order.converted_kind}
                  id={order.converted_ref_id}
                  testId="planning-drawer-document"
                />
                {order.converted_at !== null ? (
                  <span className="planning-muted">
                    {t("planning.orders.drawer.byAt", {
                      who: who(order.converted_by),
                      time: formatTimestamp(order.converted_at, i18n.language, zone),
                    })}
                  </span>
                ) : null}
              </span>
            </Fact>
          ) : null}
          {order.cancelled_kind !== null ? (
            <Fact label={t("planning.orders.drawer.cancelled")}>
              <span data-testid="planning-drawer-cancelled" data-kind={order.cancelled_kind}>
                {t(`planning.orders.cancelledKind.${order.cancelled_kind}`)}
                {order.cancelled_reason !== null ? ` · ${order.cancelled_reason}` : ""}
              </span>
            </Fact>
          ) : null}
        </dl>
        {order.stale_pin ? (
          <Banner kind="warn" data-testid="planning-drawer-stale-pin">
            {order.status === "proposed"
              ? t("planning.orders.drawer.stalePinProposed")
              : t("planning.orders.drawer.stalePinFirmed")}
          </Banner>
        ) : null}
        {order.exceeds_mps ? (
          <Banner kind="warn" data-testid="planning-drawer-exceeds-mps">
            {t("planning.orders.drawer.exceedsMps")}
          </Banner>
        ) : null}
        <OrderActions order={order} onPending={onPending} />
      </section>
      <DemandSection
        detail={detail}
        products={products.byId}
        unit={unit}
        onOpen={onOpen}
        facilityName={facilityName}
      />
      <QuantitySection detail={detail} unit={unit} labels={detail.labels} />
      <DateSection
        detail={detail}
        zone={zone}
        facilityName={facilityName}
        canReadCalendar={perms.has("planning.calendar.read")}
      />
      <DrivesSection
        edges={detail.pegs_down}
        homeFacilityId={order.facility_id}
        products={products.byId}
        unit={unit}
        onOpen={onOpen}
        facilityName={facilityName}
      />
      <MessageBox order={order} onPending={onPending} />
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="planning-fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function LateChip() {
  const { t } = useTranslation();
  return (
    <StatusChip variant="bad" dot={false} data-testid="planning-order-late">
      {t("planning.orders.late")}
    </StatusChip>
  );
}

export function ProductName({ product, id }: { product: Product | undefined; id: string }) {
  if (product === undefined) return <span className="planning-muted">{shortId(id)}</span>;
  return (
    <span className="planning-product" data-product-id={id}>
      <span className="planning-sku">{product.sku}</span>
      <span className="planning-product-name">{product.name}</span>
    </span>
  );
}

// ---- Demand -----------------------------------------------------------------------

function labelOf(
  labels: readonly ExplanationLabel[],
  kind: string,
  id: string,
): ExplanationLabel | undefined {
  return labels.find((l) => l.ref_kind === kind && l.ref_id === id);
}

/** A demand item's or a peg's reference in words, linked where the reader may open it. */
function RefLabel({
  kind,
  id,
  label,
  onOpen,
  facilityName,
}: {
  kind: string;
  id: string | null;
  label: ExplanationLabel | undefined;
  onOpen: (id: string) => void;
  facilityName: (id: string | null) => string;
}) {
  const { t } = useTranslation();
  if (kind === "sales_order_line") {
    return <OrderLabel label={label?.sales_order ?? null} testId="planning-drawer-ref" />;
  }
  if ((kind === "planned_order" || kind === "transfer_planned_order") && id !== null) {
    const number = label?.number ?? null;
    const words =
      kind === "transfer_planned_order"
        ? t("planning.orders.ref.transferOrder", {
            number: number ?? "",
            facility: facilityName(label?.facility_id ?? null),
          })
        : t("planning.orders.ref.parentOrder", { number: number ?? "" });
    if (label?.reduced === true || number === null) {
      return (
        <span className="planning-muted" data-testid="planning-drawer-ref" data-reduced="true">
          {words}
        </span>
      );
    }
    return (
      <button
        type="button"
        className="planning-linklike"
        data-testid="planning-drawer-ref"
        data-order-id={id}
        onClick={() => {
          onOpen(id);
        }}
      >
        {words}
      </button>
    );
  }
  if (kind === "mps_line") {
    return (
      <Link to="/planning/mps" data-testid="planning-drawer-ref" data-kind={kind}>
        {t("planning.orders.ref.mpsCell", { number: label?.number ?? "" })}
      </Link>
    );
  }
  if (kind === "work_order" && id !== null) {
    return (
      <Link
        to="/production/work-orders/$id"
        params={{ id }}
        data-testid="planning-drawer-ref"
        data-kind={kind}
      >
        {t("planning.orders.ref.workOrder", { number: label?.number ?? shortId(id) })}
      </Link>
    );
  }
  return (
    <span data-testid="planning-drawer-ref" data-kind={kind}>
      {t(`planning.orders.ref.${kind === "none" ? "excess" : kind}`)}
    </span>
  );
}

function DemandSection({
  detail,
  products,
  unit,
  onOpen,
  facilityName,
}: {
  detail: PlannedOrderDetail;
  products: ReadonlyMap<string, Product>;
  unit: string | undefined;
  onOpen: (id: string) => void;
  facilityName: (id: string | null) => string;
}) {
  const { t, i18n } = useTranslation();
  const demand = detail.explanation.demand;
  const up = detail.pegs_up;
  return (
    <section
      className="planning-drawer-section"
      aria-labelledby="planning-drawer-demand-h"
      data-testid="planning-drawer-demand"
    >
      <h2 id="planning-drawer-demand-h">{t("planning.orders.drawer.demand")}</h2>
      {demand.length === 0 ? (
        <p className="planning-note">{t("planning.orders.drawer.noDemand")}</p>
      ) : (
        <ul className="planning-hops">
          {demand.map((item, i) => (
            <li
              key={`${item.source_ref_kind}-${item.source_ref_id}-${String(i)}`}
              className="planning-hop"
              data-testid="planning-drawer-demand-item"
              data-ref-kind={item.source_ref_kind}
            >
              <RefLabel
                kind={item.source_ref_kind}
                id={item.source_ref_id}
                label={labelOf(detail.labels, item.source_ref_kind, item.source_ref_id)}
                onOpen={onOpen}
                facilityName={facilityName}
              />
              <ExactQty qty={item.qty} unit={unit} />
              <span className="planning-muted">
                {t("planning.orders.drawer.neededBy", {
                  date: formatDate(item.need_date, i18n.language),
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
      {up.length > 0 ? (
        <>
          <h3 className="planning-drawer-sub">{t("planning.orders.drawer.chainUp")}</h3>
          <PegList
            edges={up}
            products={products}
            unit={unit}
            onOpen={onOpen}
            facilityName={facilityName}
            homeFacilityId={detail.order.facility_id}
            testId="planning-drawer-peg-up"
          />
        </>
      ) : null}
      {detail.walk_hidden > 0 ? (
        <p className="planning-note" data-testid="planning-drawer-walk-hidden">
          {t("planning.orders.drawer.walkHidden", { count: detail.walk_hidden })}
        </p>
      ) : null}
    </section>
  );
}

/** One edge of the walk: an order the reader may open, a reduced hop, or a pool entry. */
function PegList({
  edges,
  products,
  unit,
  onOpen,
  facilityName,
  homeFacilityId,
  testId,
}: {
  edges: readonly PegEdge[];
  homeFacilityId: string;
  products: ReadonlyMap<string, Product>;
  unit: string | undefined;
  onOpen: (id: string) => void;
  facilityName: (id: string | null) => string;
  testId: string;
}) {
  const { t, i18n } = useTranslation();
  return (
    <ul className="planning-hops">
      {edges.map((edge, i) => {
        const indent = Math.min(edge.depth - 1, 4);
        const common = {
          className: `planning-hop planning-hop-d${String(indent)}${edge.reduced !== null ? " planning-hop-reduced" : ""}`,
          "data-testid": testId,
          "data-depth": edge.depth,
          "data-source-kind": edge.source_kind,
        };
        if (edge.reduced !== null) {
          const r = edge.reduced;
          return (
            <li key={String(i)} {...common} data-reduced="true">
              <span>{r.facility_name}</span>
              <span>{r.po_no}</span>
              <span>{r.kind}</span>
              <span>{r.product_sku ?? shortId(r.product_id)}</span>
              <ExactQty qty={r.qty} />
            </li>
          );
        }
        if (edge.order !== null) {
          const o = edge.order;
          return (
            <li key={String(i)} {...common} data-order-id={o.id}>
              <button
                type="button"
                className="planning-linklike"
                onClick={() => {
                  onOpen(o.id);
                }}
              >
                {o.po_no}
              </button>
              <PlannedOrderKindChip kind={o.kind} />
              <ProductName product={products.get(o.product_id)} id={o.product_id} />
              <ExactQty qty={edge.qty} unit={unit} />
              <span className="planning-muted">{formatDate(o.due_date, i18n.language)}</span>
              {o.facility_id !== homeFacilityId ? (
                <span className="planning-muted">{facilityName(o.facility_id)}</span>
              ) : null}
            </li>
          );
        }
        return (
          <li key={String(i)} {...common}>
            <span>
              {t(
                `planning.orders.ref.${edge.source_ref_kind === "none" ? "excess" : edge.source_ref_kind}`,
              )}
            </span>
            <ExactQty qty={edge.qty} unit={unit} />
            {edge.need_date !== null ? (
              <span className="planning-muted">{formatDate(edge.need_date, i18n.language)}</span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

// ---- Quantity math ---------------------------------------------------------------

function itemLabelText(
  t: TFunction,
  item: ExplanationDemandItem,
  label: ExplanationLabel | undefined,
): string {
  const kind = item.source_ref_kind;
  if (kind === "sales_order_line") {
    const so = label?.sales_order;
    if (so?.order_no != null) {
      return so.line_no !== null
        ? `${so.order_no} · ${t("planning.demand.lineNo", { line: so.line_no })}`
        : so.order_no;
    }
    return t("planning.demand.salesOrder");
  }
  if (kind === "planned_order")
    return t("planning.orders.ref.parentOrder", { number: label?.number ?? "" });
  if (kind === "transfer_planned_order")
    return t("planning.orders.ref.transferOrderShort", { number: label?.number ?? "" });
  if (kind === "mps_line") return t("planning.orders.ref.mpsCell", { number: label?.number ?? "" });
  if (kind === "work_order")
    return t("planning.orders.ref.workOrder", { number: label?.number ?? "" });
  return t(`planning.orders.ref.${kind}`);
}

function QuantitySection({
  detail,
  unit,
  labels,
}: {
  detail: PlannedOrderDetail;
  unit: string | undefined;
  labels: readonly ExplanationLabel[];
}) {
  const { t } = useTranslation();
  const explanation = detail.explanation;
  const rows = useMemo(() => quantityWaterfall(explanation), [explanation]);
  const notes = explanation.qty_math.notes;
  const mps = explanation.qty_math.mps;
  const rowLabel = (row: WaterfallRow): string => {
    if (row.label === "item" && row.item !== undefined) {
      const item = explanation.demand[row.item];
      if (item === undefined) return "";
      return itemLabelText(t, item, labelOf(labels, item.source_ref_kind, item.source_ref_id));
    }
    return t(`planning.orders.waterfall.${row.label}`, row.params ?? {});
  };
  return (
    <section
      className="planning-drawer-section"
      aria-labelledby="planning-drawer-qty-h"
      data-testid="planning-drawer-qty"
    >
      <h2 id="planning-drawer-qty-h">{t("planning.orders.drawer.qtyMath")}</h2>
      <div
        className="planning-waterfall"
        role="list"
        aria-label={t("planning.orders.drawer.qtyMath")}
      >
        <div className="planning-wf-row planning-wf-head" aria-hidden="true">
          <span>{t("planning.orders.waterfall.step")}</span>
          <span>{t("planning.orders.waterfall.before")}</span>
          <span>{t("planning.orders.waterfall.term")}</span>
          <span>
            {t("planning.orders.waterfall.after")}
            {unit !== undefined ? ` (${unit})` : ""}
          </span>
        </div>
        {rows.map((row) => (
          <div
            key={row.key}
            role="listitem"
            className={[
              "planning-wf-row",
              `planning-wf-i${String(row.indent)}`,
              row.total === true ? "planning-wf-total" : "",
              row.partOf === true ? "planning-wf-part" : "",
              row.term !== undefined && isZeroQty(row.term) ? "planning-wf-zero" : "",
            ]
              .filter((c) => c.length > 0)
              .join(" ")}
            data-testid="planning-wf-row"
            data-step={row.key}
            data-after={row.after}
          >
            <span className="planning-wf-label">{rowLabel(row)}</span>
            <span className="planning-wf-num" data-label={t("planning.orders.waterfall.before")}>
              {row.before !== undefined ? <ExactQty qty={row.before} /> : null}
            </span>
            <span className="planning-wf-num" data-label={t("planning.orders.waterfall.term")}>
              {row.term !== undefined ? <WaterfallTerm row={row} /> : null}
            </span>
            <span className="planning-wf-num" data-label={t("planning.orders.waterfall.after")}>
              {row.after !== undefined ? <ExactQty qty={row.after} /> : null}
            </span>
          </div>
        ))}
      </div>
      {mps !== null && mps.excess_sources.length > 0 ? (
        <ul className="planning-hops" data-testid="planning-drawer-mps-excess">
          {mps.excess_sources.map((s, i) => (
            <li key={String(i)} className="planning-hop planning-hop-d1">
              <span>{t(`planning.orders.ref.${s.source_ref_kind}`)}</span>
              <ExactQty qty={s.qty} unit={unit} />
            </li>
          ))}
        </ul>
      ) : null}
      <NoteList notes={notes} known={QTY_NOTES} testId="planning-drawer-qty-note" />
    </section>
  );
}

function WaterfallTerm({ row }: { row: WaterfallRow }) {
  const term = row.term ?? "";
  if (row.op === "minus") {
    return (
      <span className="planning-wf-minus">
        <span aria-hidden="true">− </span>
        <ExactQty qty={term} />
      </span>
    );
  }
  if (row.op === "percent") {
    return (
      <span>
        <ExactQty qty={term} /> %
      </span>
    );
  }
  if (row.op === "per") {
    return (
      <span>
        ÷ <ExactQty qty={term} />
      </span>
    );
  }
  return <ExactQty qty={term} />;
}

function NoteList({
  notes,
  known,
  testId,
}: {
  notes: readonly string[];
  known: ReadonlySet<string>;
  testId: string;
}) {
  const { t } = useTranslation();
  if (notes.length === 0) return null;
  return (
    <ul className="planning-notes">
      {notes.map((key) => (
        <li key={key} data-testid={testId} data-note={key}>
          {known.has(key) ? t(`planning.orders.note.${key}`) : key}
        </li>
      ))}
    </ul>
  );
}

// ---- Date math --------------------------------------------------------------------

function DateSection({
  detail,
  zone,
  facilityName,
  canReadCalendar,
}: {
  detail: PlannedOrderDetail;
  zone: string | null | undefined;
  facilityName: (id: string | null) => string;
  canReadCalendar: boolean;
}) {
  const { t, i18n } = useTranslation();
  const dm = detail.explanation.date_math;
  const rows = useMemo(() => dateTimeline(dm), [dm]);
  const order = detail.order;
  const workCenters = useCalendarWorkCenters(order.facility_id);
  const wcName = (id: string) => {
    const w = workCenters.data?.find((x) => x.id === id);
    return w === undefined ? shortId(id) : workCenterLabel(w, i18n.language);
  };
  // The exceptions that closed the skipped dates, read only by a reader who may
  // read the calendars; anyone else is told how many there were.
  const dates = [...dm.skipped_dates, dm.release_date, dm.due_date].sort();
  const from = dates[0] ?? dm.release_date;
  const to = dates[dates.length - 1] ?? dm.due_date;
  const wantExceptions = canReadCalendar && dm.exception_ids.length > 0;
  const exceptions = useCalendarExceptions(
    order.facility_id,
    { from, to },
    undefined,
    wantExceptions,
  );
  const named = (exceptions.data?.items ?? []).filter((e) => dm.exception_ids.includes(e.id));
  const closersOf = (date: string) =>
    named.filter((e) => {
      const a = facilityTodayIso(Date.parse(e.starts_at), zone);
      const b = facilityTodayIso(Date.parse(e.ends_at) - 1, zone);
      return a <= date && date <= b;
    });

  const offsetText = (row: TimelineRow): string => {
    const data = row.data ?? {};
    switch (row.kind) {
      case "routing_time":
        return t("planning.orders.timeline.routing_time", {
          days: row.days ?? 0,
          ops: typeof data["ops"] === "number" ? data["ops"] : 0,
          minutes: typeof data["minutes"] === "string" ? data["minutes"] : "",
          centres: Array.isArray(data["work_center_ids"])
            ? (data["work_center_ids"] as unknown[])
                .filter((x): x is string => typeof x === "string")
                .map(wcName)
                .join(", ")
            : "",
        });
      case "source_snap":
        return t("planning.orders.timeline.source_snap", {
          days: row.days ?? 0,
          facility: facilityName(
            typeof data["source_facility_id"] === "string" ? data["source_facility_id"] : null,
          ),
        });
      case "due_snap":
      case "lead_time_days":
      case "release_buffer":
      case "version_repicked":
        return t(`planning.orders.timeline.${row.kind}`, { days: row.days ?? 0 });
      default:
        return row.kind;
    }
  };

  return (
    <section
      className="planning-drawer-section"
      aria-labelledby="planning-drawer-date-h"
      data-testid="planning-drawer-date"
    >
      <h2 id="planning-drawer-date-h">{t("planning.orders.drawer.dateMath")}</h2>
      <ol className="planning-timeline">
        {rows.map((row) => (
          <li
            key={row.key}
            className={row.date !== undefined ? "planning-tl-date" : "planning-tl-offset"}
            data-testid="planning-tl-row"
            data-kind={row.kind}
            data-date={row.date}
          >
            {row.date !== undefined ? (
              <>
                <span className="planning-tl-label">
                  {t(`planning.orders.timeline.${row.kind}`)}
                </span>
                <span className="planning-tl-value">
                  {row.original !== undefined ? (
                    <s className="planning-muted">{formatDate(row.original, i18n.language)}</s>
                  ) : null}
                  <span>{formatDate(row.date, i18n.language)}</span>
                  {row.original !== undefined ? (
                    <StatusChip variant="warn" dot={false} data-testid="planning-tl-clamped">
                      {t("planning.orders.timeline.clamped")}
                    </StatusChip>
                  ) : null}
                  {row.kind === "due" && dm.late ? <LateChip /> : null}
                </span>
              </>
            ) : (
              <span className="planning-tl-label">{offsetText(row)}</span>
            )}
            {row.skipped.length > 0 ? (
              <ul className="planning-tl-skipped" data-testid="planning-tl-skipped">
                {row.skipped.map((d) => (
                  <li key={d} data-date={d}>
                    {t("planning.orders.timeline.skippedDate", {
                      date: formatDate(d, i18n.language),
                    })}
                    {closersOf(d).map((e) => (
                      <span key={e.id} className="planning-muted" data-exception-id={e.id}>
                        {" "}
                        {t(`planning.calendars.exceptions.kinds.${e.kind}`)}
                        {e.note !== null && e.note.length > 0 ? ` · ${e.note}` : ""}
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ol>
      {dm.exception_ids.length > 0 && named.length === 0 ? (
        <p className="planning-note" data-testid="planning-drawer-exception-count">
          {t("planning.orders.timeline.exceptions", { count: dm.exception_ids.length })}
        </p>
      ) : null}
      <NoteList notes={dm.notes} known={DATE_NOTES} testId="planning-drawer-date-note" />
    </section>
  );
}

// ---- Drives -----------------------------------------------------------------------

function DrivesSection({
  edges,
  products,
  unit,
  onOpen,
  facilityName,
  homeFacilityId,
}: {
  edges: readonly PegEdge[];
  homeFacilityId: string;
  products: ReadonlyMap<string, Product>;
  unit: string | undefined;
  onOpen: (id: string) => void;
  facilityName: (id: string | null) => string;
}) {
  const { t } = useTranslation();
  return (
    <section
      className="planning-drawer-section"
      aria-labelledby="planning-drawer-drives-h"
      data-testid="planning-drawer-drives"
    >
      <h2 id="planning-drawer-drives-h">{t("planning.orders.drawer.drives")}</h2>
      {edges.length === 0 ? (
        <p className="planning-note">{t("planning.orders.drawer.drivesNone")}</p>
      ) : (
        <PegList
          edges={edges}
          products={products}
          unit={unit}
          onOpen={onOpen}
          facilityName={facilityName}
          homeFacilityId={homeFacilityId}
          testId="planning-drawer-peg-down"
        />
      )}
    </section>
  );
}

// ---- Firm and cancel, and the action-message box ------------------------------------

function useWrite(order: PlannedOrder, onPending: (busy: boolean) => void) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const refresh = useMrpRefresh();
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<{ kind: "ok" | "bad"; text: string } | undefined>();
  /**
   * Runs one write. A write that answers a refusal code (a per-id refusal in a
   * bulk answer) reads as that refusal; a thrown problem reads through
   * `refused`, and anything else as the save failure with its request id.
   */
  const run = async (
    write: () => Promise<string | { readonly text: string } | undefined>,
    ok: string,
    refused: (e: RequestError) => string | undefined,
  ) => {
    setBusy(true);
    onPending(true);
    setOutcome(undefined);
    try {
      const code = await write();
      setOutcome(
        code === undefined
          ? { kind: "ok", text: ok }
          : { kind: "bad", text: typeof code === "string" ? refusalText(t, code) : code.text },
      );
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      const words = error instanceof RequestError ? refused(error) : undefined;
      setOutcome({
        kind: "bad",
        text: words ?? t("planning.common.saveFailed", { requestId: requestIdOf(error) ?? "-" }),
      });
    } finally {
      setBusy(false);
      onPending(false);
      void refresh(order.facility_id, [order.id]);
    }
  };
  return { busy, outcome, run, setOutcome };
}

function OrderActions({
  order,
  onPending,
}: {
  order: PlannedOrder;
  onPending: (busy: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const perms = usePlanningPermissions();
  const offline = useOffline();
  const write = useWrite(order, onPending);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");
  const canManage = perms.has("planning.planned_order.manage");
  // Convert (P3-H-3, §3.4) is offered on a firmed order to a holder of its own
  // code, here as on the bulk bar, because a phone has no bulk bar.
  const convertible = order.status === "firmed" && perms.has("planning.planned_order.convert");
  if (!canManage && !convertible) return null;
  if (order.status !== "proposed" && order.status !== "firmed") return null;
  const trimmed = reason.trim();
  const convert = () =>
    void write.run(
      async () => {
        const res = await convertPlannedOrders([order.id]);
        const one = res.results[0];
        if (one === undefined || one.status === "converted") return undefined;
        const code = one.refusal?.code ?? "other";
        const extra =
          one.refusal?.reason !== undefined
            ? ` ${t(`planning.orders.pin.${one.refusal.reason}`)}`
            : code === "PLANNED_ORDER_NO_ROUTING" && one.refusal?.start_date !== undefined
              ? ` ${t("planning.orders.noRoutingOn", {
                  date: formatDate(one.refusal.start_date, i18n.language),
                })}`
              : "";
        return { text: `${refusalText(t, code)}${extra}` };
      },
      t("planning.orders.drawer.convertedDone", { number: order.po_no }),
      (e) => (e.code !== undefined ? refusalText(t, e.code) : undefined),
    );
  const convertControl = !convertible ? null : order.kind === "purchase" ? (
    <p className="planning-note" data-testid="planning-drawer-convert-buy">
      {t("planning.orders.drawer.convertBuy")}
    </p>
  ) : (
    <Button
      variant={canManage ? "secondary" : "primary"}
      busy={write.busy}
      busyLabel={t("planning.common.saving")}
      disabled={offline}
      data-testid="planning-drawer-convert"
      onClick={convert}
    >
      {t("planning.orders.convert")}
    </Button>
  );
  return (
    <div className="planning-order-actions" data-testid="planning-drawer-actions">
      {!canManage ? null : order.status === "proposed" ? (
        <Button
          variant="primary"
          busy={write.busy}
          busyLabel={t("planning.common.saving")}
          disabled={offline}
          data-testid="planning-drawer-firm"
          onClick={() =>
            void write.run(
              async () => {
                const res = await firmPlannedOrders([order.id]);
                const one = res.results[0];
                return one?.result === null ? one.refusal?.code : undefined;
              },
              t("planning.orders.drawer.firmedDone", { number: order.po_no }),
              (e) => (e.code !== undefined ? refusalText(t, e.code) : undefined),
            )
          }
        >
          {t("planning.orders.firm")}
        </Button>
      ) : cancelling ? (
        <div className="planning-confirm" data-testid="planning-drawer-cancel-form">
          <Field label={t("planning.orders.cancelReason")} wide>
            <textarea
              rows={2}
              maxLength={PLANNED_ORDER_REASON_MAX}
              value={reason}
              data-testid="planning-drawer-cancel-reason"
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </Field>
          <div className="planning-actions">
            <Button
              disabled={write.busy}
              onClick={() => {
                setCancelling(false);
              }}
            >
              {t("planning.common.cancel")}
            </Button>
            <Button
              variant="danger"
              busy={write.busy}
              busyLabel={t("planning.common.saving")}
              disabled={offline || trimmed.length === 0}
              data-testid="planning-drawer-cancel-confirm"
              onClick={() =>
                void write.run(
                  async () => {
                    await cancelPlannedOrder(order.id, trimmed);
                    return undefined;
                  },
                  t("planning.orders.drawer.cancelledDone", { number: order.po_no }),
                  (e) => (e.code !== undefined ? refusalText(t, e.code) : undefined),
                )
              }
            >
              {t("planning.orders.cancelOrder")}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="danger"
          disabled={offline}
          data-testid="planning-drawer-cancel"
          onClick={() => {
            setCancelling(true);
          }}
        >
          {t("planning.orders.cancelOrder")}
        </Button>
      )}
      {cancelling ? null : convertControl}
      {offline ? <p className="planning-note">{t("planning.common.offlineWrites")}</p> : null}
      {write.outcome !== undefined ? (
        <Banner kind={write.outcome.kind} data-testid="planning-drawer-action-outcome">
          {write.outcome.text}
        </Banner>
      ) : null}
    </div>
  );
}

/** A refusal code in words; an unknown code reads as itself. */
export function refusalText(t: TFunction, code: string): string {
  const known = [
    "PLANNED_ORDER_NOT_PROPOSED",
    "PLANNED_ORDER_NOT_FIRMED",
    "PLANNED_ORDER_NOT_FOUND",
    "NO_ACTION_MESSAGE",
    "ACTION_MESSAGE_CHANGED",
    "ACTION_MESSAGE_NOT_BULK_ACCEPTABLE",
    "PERMISSION_DENIED",
    // The conversion's refusals (§3.4, §5 row 50): planning's own, module
    // 09's peg problems and module 06's draft transfer's.
    "PURCHASE_CONVERSION_UNAVAILABLE",
    "PLANNED_ORDER_NO_ROUTING",
    "PLANNED_ORDER_PIN_INVALID",
    "ORDER_LINE_NOT_FOUND",
    "ORDER_NOT_CONFIRMED",
    "ORDER_TERMINAL",
    "ORDER_LINE_TERMINAL",
    "ORDER_LINE_PRODUCT_MISMATCH",
    "TRANSFER_SOURCE_INACTIVE",
    "FACILITY_NOT_FOUND",
    "PRODUCT_NOT_FOUND",
  ];
  return known.includes(code)
    ? t(`planning.orders.refusal.${code}`)
    : t("planning.orders.refusal.other", { code });
}

/** A message's suggestion in words: the date or the quantity it names, or nothing. */
export function suggestionText(
  t: TFunction,
  locale: string,
  message: ExpectedActionMessage,
): string {
  if (message.suggestion === "") return t(`planning.orders.suggest.${message.kind}`);
  if (/^\d{4}-\d{2}-\d{2}$/u.test(message.suggestion))
    return t(`planning.orders.suggest.${message.kind}`, {
      value: formatDate(message.suggestion, locale),
    });
  return t(`planning.orders.suggest.${message.kind}`, { value: message.suggestion });
}

function MessageBox({
  order,
  onPending,
}: {
  order: PlannedOrder;
  onPending: (busy: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const perms = usePlanningPermissions();
  const offline = useOffline();
  const write = useWrite(order, onPending);
  const [step, setStep] = useState<"idle" | "confirm" | "dismiss">("idle");
  const [reason, setReason] = useState("");
  const [changed, setChanged] = useState<ExpectedActionMessage | null | undefined>(undefined);
  const expected = order.action_message_expected;
  const detail = order.action_message_detail;
  const dismissal = order.action_message_dismissal;
  if (order.action_message === null || expected === null) {
    return dismissal !== null ? (
      <section className="planning-drawer-section" data-testid="planning-drawer-message">
        <h2>{t("planning.orders.drawer.message")}</h2>
        <p className="planning-note" data-testid="planning-drawer-dismissed">
          {t("planning.orders.drawer.lastDismissed", {
            message: suggestionText(t, i18n.language, dismissal),
            reason: dismissal.reason,
          })}
        </p>
        {write.outcome !== undefined ? (
          <Banner kind={write.outcome.kind} data-testid="planning-drawer-message-outcome">
            {write.outcome.text}
          </Banner>
        ) : null}
      </section>
    ) : write.outcome !== undefined ? (
      <Banner kind={write.outcome.kind} data-testid="planning-drawer-message-outcome">
        {write.outcome.text}
      </Banner>
    ) : null;
  }
  const canManage = perms.has("planning.planned_order.manage");
  const refused = (e: RequestError): string | undefined => {
    if (e.code === "ACTION_MESSAGE_CHANGED") {
      const current = e.problem as { current_message?: ExpectedActionMessage | null } | undefined;
      setChanged(current?.current_message ?? null);
    }
    return e.code !== undefined ? refusalText(t, e.code) : undefined;
  };
  const accept = () =>
    void write.run(
      async () => {
        await acceptActionMessage(order.id, expected);
        return undefined;
      },
      t("planning.orders.drawer.acceptedDone", { number: order.po_no }),
      refused,
    );
  const reasonKey = detail?.reason_key;
  return (
    <section
      className="planning-drawer-section planning-message-box"
      aria-labelledby="planning-drawer-message-h"
      data-testid="planning-drawer-message"
      data-kind={order.action_message}
    >
      <h2 id="planning-drawer-message-h">{t("planning.orders.drawer.message")}</h2>
      <p className="planning-message-line">
        <ActionMessageChip kind={order.action_message} />
        <span data-testid="planning-drawer-suggestion">
          {suggestionText(t, i18n.language, expected)}
        </span>
      </p>
      {reasonKey !== undefined ? (
        <p className="planning-note" data-testid="planning-drawer-reason" data-reason={reasonKey}>
          {REASON_KEYS.has(reasonKey) ? t(`planning.orders.reason.${reasonKey}`) : reasonKey}
          {detail?.delta_days !== undefined && detail.delta_days !== 0
            ? ` ${t("planning.orders.drawer.deltaDays", { count: detail.delta_days })}`
            : ""}
        </p>
      ) : null}
      {changed !== undefined ? (
        <p className="planning-note" data-testid="planning-drawer-message-changed">
          {changed === null
            ? t("planning.orders.drawer.messageGone")
            : t("planning.orders.drawer.messageNow", {
                message: suggestionText(t, i18n.language, changed),
              })}
        </p>
      ) : null}
      {canManage && order.status === "firmed" ? (
        step === "dismiss" ? (
          <div className="planning-confirm" data-testid="planning-drawer-dismiss-form">
            <Field label={t("planning.orders.dismissReason")} wide>
              <textarea
                rows={2}
                maxLength={PLANNED_ORDER_REASON_MAX}
                value={reason}
                data-testid="planning-drawer-dismiss-reason"
                onChange={(e) => {
                  setReason(e.target.value);
                }}
              />
            </Field>
            <div className="planning-actions">
              <Button
                disabled={write.busy}
                onClick={() => {
                  setStep("idle");
                }}
              >
                {t("planning.common.cancel")}
              </Button>
              <Button
                busy={write.busy}
                busyLabel={t("planning.common.saving")}
                disabled={offline || reason.trim().length === 0}
                data-testid="planning-drawer-dismiss-confirm"
                onClick={() =>
                  void write.run(
                    async () => {
                      await dismissActionMessage(order.id, expected, reason.trim());
                      return undefined;
                    },
                    t("planning.orders.drawer.dismissedDone", { number: order.po_no }),
                    refused,
                  )
                }
              >
                {t("planning.orders.dismiss")}
              </Button>
            </div>
          </div>
        ) : step === "confirm" ? (
          <div className="planning-confirm" data-testid="planning-drawer-accept-confirm-box">
            <p>
              {t("planning.orders.drawer.acceptConfirm", {
                message: suggestionText(t, i18n.language, expected),
              })}
            </p>
            <div className="planning-actions">
              <Button
                disabled={write.busy}
                onClick={() => {
                  setStep("idle");
                }}
              >
                {t("planning.common.cancel")}
              </Button>
              <Button
                variant="primary"
                busy={write.busy}
                busyLabel={t("planning.common.saving")}
                disabled={offline}
                data-testid="planning-drawer-accept-confirm"
                onClick={accept}
              >
                {t("planning.orders.accept")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="planning-actions">
            <Button
              variant="primary"
              disabled={offline}
              data-testid="planning-drawer-accept"
              onClick={() => {
                // Bringing an order in, expediting it, changing its quantity or
                // cancelling it is confirmed per order; only a move out is
                // taken on one press, the safe direction (§3.8).
                if (order.action_message === "reschedule_out") accept();
                else setStep("confirm");
              }}
            >
              {t("planning.orders.accept")}
            </Button>
            <Button
              disabled={offline}
              data-testid="planning-drawer-dismiss"
              onClick={() => {
                setStep("dismiss");
              }}
            >
              {t("planning.orders.dismiss")}
            </Button>
          </div>
        )
      ) : null}
      {offline && canManage ? (
        <p className="planning-note">{t("planning.common.offlineWrites")}</p>
      ) : null}
      {write.outcome !== undefined ? (
        <Banner kind={write.outcome.kind} data-testid="planning-drawer-message-outcome">
          {write.outcome.text}
        </Banner>
      ) : null}
    </section>
  );
}
